package com.loyola.acessoescola.ui


import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import kotlin.io.encoding.Base64
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

expect suspend fun loadImageBitMap(uri: String): ImageBitmap?


expect suspend fun readBytesFromURI(uri: String): ByteArray?

@OptIn(ExperimentalUuidApi::class)
fun uuid(): String {
    val bytes = Uuid.random().toByteArray()
    return Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT).encode(bytes)
}


class CPFVisualTransformation: VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val out = buildString {
            for (i in text.indices) {
                append(text[i])
                if (i == 2 || i == 5) append('.')
                if (i == 8) append('-')
            }
        }

        val cpfOffsetMapping = object : OffsetMapping {
            // De posição no texto "cru" -> posição no texto "mascarado"
            override fun originalToTransformed(offset: Int): Int {
                // Há até 3 caracteres de máscara antes das posições:
                // após 3 dígitos: +1, após 6 dígitos: +2, após 9 dígitos: +3
                if (offset <= 2) return offset
                if (offset <= 5) return offset + 1
                if (offset <= 8) return offset + 2
                if (offset <= 11) return offset + 3
                return 14 // tamanho máximo "###.###.###-##"
            }

            // De posição no texto "mascarado" -> posição no texto "cru"
            override fun transformedToOriginal(offset: Int): Int {
                if (offset <= 3) return offset
                if (offset <= 7) return offset - 1
                if (offset <= 11) return offset - 2
                if (offset <= 14) return offset - 3
                return 11
            }
        }

        return TransformedText(AnnotatedString(out), cpfOffsetMapping)

    }
}

fun isValidCPF(cpf: String): Boolean {
    if (cpf.length != 11) return false
    if ((0..9).any { d -> cpf.all { it == ('0' + d) } }) return false // rejeita "111...","000..."

    fun dv(cpf: String, length: Int): Int {
        val sum = cpf.take(length).mapIndexed { i, c ->
            val weight = (length + 1) - i
            (c - '0') * weight
        }.sum()
        val r = (sum * 10) % 11
        return if (r == 10) 0 else r
    }

    val d1 = dv(cpf, 9)
    val d2 = dv(cpf, 10)
    return d1 == (cpf[9] - '0') && d2 == (cpf[10] - '0')
}


class CellphoneVisualTransformation : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        // Constrói a máscara
        val out = buildString {
            for (i in text.indices) {
                when (i) {
                    0 -> append("(").append(text[i])
                    1 -> append(text[i]).append(") ")
                    6 -> { // depois do 7º dígito (índice 6), inserir hífen
                        append(text[i]).append("-")
                    }
                    else -> append(text[i])
                }
            }
        }

        // Mapeamento do cursor: posições do texto cru (0..11) <-> mascarado (0..15)
        val offsetMapping = object : OffsetMapping {
            // original (sem máscara) -> transformado (com máscara)
            override fun originalToTransformed(offset: Int): Int {
                // Mapa de acréscimos: "(" antes do 1º, ")" e espaço após 2º, "-" após 7º
                val maskChars: Int = out.filter { !it.isDigit() }.length
                return offset + maskChars
            }

            // transformado (com máscara) -> original (sem máscara)
            override fun transformedToOriginal(offset: Int): Int {
                return when {
                    offset <= 0 -> 0 // ""
                    offset in 2..3 ->(offset - 1)// "(#".."(##"
                    offset in 6..10 ->(offset - 3)// "(##) #".."(##) #####"
                    offset in 12..15 ->(offset - 4)// "(##) #".."(##) #####"
                    else -> text.length
                }
            }
        }

        return TransformedText(AnnotatedString(out), offsetMapping)
    }
}
